import { useMutation } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";

import { Dialog, Select, type SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  type Curriculum,
  type OrganizationSummary,
  type TreatmentPlanTemplateData,
  updateTemplate,
  useListTemplateCurricula,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { AIGenerationConfig } from "@pages/Templates/constants";

// --- Treatment Plan Settings Dialog ---

const AI_GENERATION_CONFIG_LABELS: Record<string, string> = {
  [AIGenerationConfig.GOALS_AND_TARGETS]: "Goals & Targets",
  [AIGenerationConfig.GOALS_ONLY]: "Goals Only",
  [AIGenerationConfig.NO_AI_GENERATION]: "No AI Generation",
};

const AI_GENERATION_OPTIONS: SelectItem[] = Object.entries(AI_GENERATION_CONFIG_LABELS).map(([id, primary]) => ({
  id,
  primary,
}));

export const TreatmentPlanSettingsDialog: FC<{
  open: boolean;
  onClose: () => void;
  tpt: TreatmentPlanTemplateData;
  currentCurriculum: Curriculum | null;
  templateId: number;
  organizations: readonly OrganizationSummary[];
  onSaved: () => void;
}> = ({ open, onClose, tpt, currentCurriculum, templateId, organizations, onSaved }) => {
  const [aiConfig, setAiConfig] = useState(tpt.ai_generation_config);
  const [isGoalBank, setIsGoalBank] = useState(tpt.is_goal_bank_suggestions_enabled);
  const [isAddTargets, setIsAddTargets] = useState(tpt.is_add_targets_enabled);
  const [isMedical, setIsMedical] = useState(tpt.is_medical_necessity_enabled);
  const [isGas, setIsGas] = useState(tpt.is_gas_enabled);
  const [selectedCurriculum, setSelectedCurriculum] = useState<SelectItem | undefined>(
    currentCurriculum ? { id: String(currentCurriculum.id), primary: currentCurriculum.name } : undefined,
  );
  const [selectedOrg, setSelectedOrg] = useState<SelectItem | undefined>(() => {
    if (!currentCurriculum || organizations.length <= 1) return undefined;
    return { id: String(currentCurriculum.organization_id), primary: currentCurriculum.organization_name };
  });

  const curriculaQuery = useListTemplateCurricula(templateId, {
    query: { enabled: open },
  });
  const allCurricula = useMemo(
    () => (curriculaQuery.data?.status === 200 ? curriculaQuery.data.data : []),
    [curriculaQuery.data],
  );

  const orgItems = useMemo<SelectItem[]>(
    () => organizations.map((o) => ({ id: String(o.id), primary: o.name })),
    [organizations],
  );

  const multipleOrgs = orgItems.length > 1;

  const curriculumItems = useMemo<SelectItem[]>(() => {
    const filtered =
      multipleOrgs && selectedOrg
        ? allCurricula.filter((c) => String(c.organization_id) === selectedOrg.id)
        : allCurricula;
    return filtered.map((c) => ({ id: String(c.id), primary: c.name }));
  }, [allCurricula, multipleOrgs, selectedOrg]);

  const aiSelected = AI_GENERATION_OPTIONS.find((item) => item.id === aiConfig);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateTemplate({
        template_id: templateId,
        treatment_plan_settings: {
          ai_generation_config: aiConfig,
          is_goal_bank_suggestions_enabled: isGoalBank,
          is_add_targets_enabled: isAddTargets,
          is_medical_necessity_enabled: isMedical,
          is_gas_enabled: isGas,
        },
        goal_bank_curriculum_id: selectedCurriculum ? Number(selectedCurriculum.id) : null,
      }),
    onSuccess: (result) => {
      if (result.status !== 200) {
        notifyError("Failed to save treatment plan settings");
        return;
      }
      notifySuccess("Treatment plan settings saved");
      onSaved();
      onClose();
    },
    onError: () => notifyError("Failed to save treatment plan settings"),
  });

  return (
    <Dialog
      title="Treatment Plan Settings"
      open={open}
      setOpen={(v) => {
        if (!v) onClose();
      }}
      primaryButton={{ text: "Save", onClick: () => saveMutation.mutate(), disabled: saveMutation.isPending }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      size="xl"
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">AI Generation</label>
          <Select
            title=""
            items={AI_GENERATION_OPTIONS}
            selected={aiSelected}
            setSelected={(item) => setAiConfig(String(item.id))}
            placeholderText="Select AI generation config"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isGoalBank}
            onChange={(e) => setIsGoalBank(e.target.checked)}
            className="h-4 w-4 rounded border-border-medium text-rio-600"
          />
          <span className="text-sm text-text-primary">Goal Bank Suggestions Enabled</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isAddTargets}
            onChange={(e) => setIsAddTargets(e.target.checked)}
            className="h-4 w-4 rounded border-border-medium text-rio-600"
          />
          <span className="text-sm text-text-primary">Add Targets Enabled</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isMedical}
            onChange={(e) => setIsMedical(e.target.checked)}
            className="h-4 w-4 rounded border-border-medium text-rio-600"
          />
          <span className="text-sm text-text-primary">Medical Necessity Enabled</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isGas}
            onChange={(e) => setIsGas(e.target.checked)}
            className="h-4 w-4 rounded border-border-medium text-rio-600"
          />
          <span className="text-sm text-text-primary">GAS Enabled</span>
        </label>
        <div className="space-y-3">
          {multipleOrgs && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Organization</label>
              <Select
                title=""
                placeholderText="Select an organization…"
                items={orgItems}
                selected={selectedOrg}
                enableSearch
                setSelected={(item) => {
                  setSelectedOrg(item);
                  if (currentCurriculum && String(currentCurriculum.organization_id) === item.id) {
                    setSelectedCurriculum({ id: String(currentCurriculum.id), primary: currentCurriculum.name });
                  } else {
                    setSelectedCurriculum(undefined);
                  }
                }}
                actionItem={
                  selectedOrg
                    ? {
                        label: "Clear",
                        onClick: () => {
                          setSelectedOrg(undefined);
                          setSelectedCurriculum(undefined);
                        },
                        closeDropdown: true,
                      }
                    : undefined
                }
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Filter Goal Bank to Folder Category
            </label>
            {(!multipleOrgs || selectedOrg) && curriculumItems.length === 0 ? (
              <p className="text-sm text-text-tertiary italic">No folders available for this organization.</p>
            ) : (
              <Select
                title=""
                placeholderText={
                  multipleOrgs && !selectedOrg ? "Select an organization first" : "All folders (Default)"
                }
                items={curriculumItems}
                selected={selectedCurriculum}
                setSelected={setSelectedCurriculum}
                actionItem={
                  selectedCurriculum
                    ? { label: "Clear filter", onClick: () => setSelectedCurriculum(undefined), closeDropdown: true }
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
